import { useState } from 'react';

import { useConfirm, useDestructiveAction } from '@/components/feedback/dialog';

import { useDeleteDiscount, useSetDiscountStatus } from './hooks';

type Row = { id: string };

/**
 * Enable/Disable orchestration shared by the discounts listing (04) and detail (07) pages.
 * Opens a `useConfirm()` dialog with the AC-pinned copy, runs the PATCH inside `onConfirm`
 * (button shows loading until it settles), and toasts the result. A 422 DISCOUNT_EXPIRED
 * (expired discount) surfaces as an error toast via `run` (ApiError message).
 */
export function useDiscountStatusAction() {
  const { confirm } = useConfirm();
  const { run } = useDestructiveAction();
  const setStatus = useSetDiscountStatus();

  const change = (row: Row, status: 'active' | 'inactive') => {
    const enabling = status === 'active';
    return confirm({
      title: enabling ? 'Enable Discount' : 'Disable Discount',
      subtitle: enabling
        ? 'This discount will become available for customer purchase.'
        : 'This discount will no longer be available for customer purchase.',
      confirmText: enabling ? 'Enable' : 'Disable',
      onConfirm: async () => {
        await run(() => setStatus.mutateAsync({ id: row.id, status }), {
          success: enabling ? 'Discount enabled successfully' : 'Discount disabled successfully',
          errorFallback: 'Failed to update discount status',
        });
      },
    });
  };

  return {
    handleEnable: (row: Row) => change(row, 'active'),
    handleDisable: (row: Row) => change(row, 'inactive'),
  };
}

/**
 * Delete orchestration shared by the listing (04) and detail (07) pages. Tracks the row
 * pending deletion and returns props to spread onto `<DeleteConfirmModal>`. Discounts have
 * no dependency-conflict (delete succeeds on Active/Inactive/Expired), so no `blockedCode`
 * is passed — `run` never resolves `{ blocked: true }` and the modal's blocked state is
 * unreachable (blockedBody is a sane fallback that never renders).
 */
export function useDiscountDeleteConfirm(opts: { onSuccess?: () => void } = {}) {
  const { run } = useDestructiveAction();
  const remove = useDeleteDiscount();
  const [deleting, setDeleting] = useState<Row | null>(null);

  const onConfirm = async (): Promise<{ blocked: true } | void> => {
    if (!deleting) return;
    return run(
      async () => {
        await remove.mutateAsync(deleting.id);
        opts.onSuccess?.(); // success only — a rejected mutateAsync skips this; run() toasts the error
      },
      {
        success: 'Discount deleted successfully',
        errorFallback: 'Failed to delete discount',
      },
    );
  };

  return {
    setDeleting,
    deleteModalProps: {
      open: deleting !== null,
      // Copy per Figma delete-confirm frame 7440-281987 (title has "?"; "Are you sure…").
      title: 'Delete Discount?',
      body: 'Are you sure you want to delete this Discount?',
      blockedBody: 'Are you sure you want to delete this Discount?',
      onConfirm,
      onClose: () => setDeleting(null),
    },
  };
}
